import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { WidgetConfigProvider, RemoteWidget } from "@tiktokshop/widget-kit-react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { getShops } from "actions/shopActions";
import { getWidgetToken } from "actions/websiteActions";
import Loading from "components/Loading";
import SelectBox from "components/elements/selectBox";

const ProductMerchantWidgets = ({ getShops, shop, getWidgetToken }) => {
    const [selectedShop, setSelectedShop] = useState("");
    const [configData, setConfigData] = useState(null);
    const [tokenData, setTokenData] = useState(null);
    const [currentWidget, setCurrentWidget] = useState("sv_overview");

    const optionRef = useRef(undefined);
    const tempOptionRef = useRef(undefined);
    const refreshTimer = useRef(null);
    const isFetchingRef = useRef(false);

    const { t } = useTranslation();
    const { list, isLoading } = shop;

    // Lấy danh sách shop
    useEffect(() => {
        getShops({ page: 1, sizeperpage: 9999 });
    }, [getShops]);

    // Fetch token theo shop
    const fetchToken = useCallback(async () => {
        if (!selectedShop || isFetchingRef.current) return;
        isFetchingRef.current = true;

        try {
            setConfigData(null);
            setTokenData(null);

            const res = await getWidgetToken(selectedShop);
            const data = res?.data;

            if (data?.widget_token?.token) {
                const { shopId, key, region, widget_token } = data;

                setConfigData({
                    shopId,
                    oecRegion: region,
                    appKey: key,
                    isvInfo: { name: "OPEN_PLATFORM" },
                });

                setTokenData(widget_token);

                // Setup refresh trước khi hết hạn 1 phút
                if (refreshTimer.current) clearTimeout(refreshTimer.current);
                const now = Math.floor(Date.now() / 1000);
                const ttl = widget_token.expire_at - now;
                if (ttl > 60) {
                    refreshTimer.current = setTimeout(fetchToken, (ttl - 60) * 1000);
                }
            } else {
                setConfigData(null);
                setTokenData(null);
            }
        } catch (err) {
            console.error("Fetch widget token failed:", err);
        } finally {
            isFetchingRef.current = false;
        }
    }, [selectedShop, getWidgetToken]);

    // Reset token khi đổi shop
    useEffect(() => {
        if (!selectedShop) return;

        setConfigData(null);
        setTokenData(null);
        fetchToken();

        return () => {
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
        };
    }, [fetchToken, selectedShop]);

    // SDK gọi lại để lấy token
    const getToken = async () => {
        if (!tokenData) return {};
        return tokenData;
    };

    // Params cho widget
    const widgetParams = useMemo(
        () => ({
            handleTabChange: (tabKey, options) => {
                if (tabKey === "product_list") {
                    // 👉 mở trang product list TikTok Shop
                    window.open("https://seller.tiktokglobalshop.com/product/list", "_blank");
                } else {
                    tempOptionRef.current = options;
                    setCurrentWidget(tabKey);
                }
            },
            options: optionRef.current,
        }),
        [currentWidget]
    );

    // Cleanup khi đổi widget
    useEffect(() => {
        return () => {
            optionRef.current = tempOptionRef.current;
            tempOptionRef.current = undefined;
        };
    }, [currentWidget]);

    return (
        <div className="mb-5 mb-xl-10">
            <Loading isLoading={isLoading} />

            {/* SelectBox chọn shop */}
            <div className="row col-md-3 mb-3">
                <label>{t("Select Shop")}</label>
                <SelectBox
                    options={list
                        ?.filter((s) => s.member_note !== "The seller is inactive.")
                        ?.map((v) => ({
                            label: `${v?.name} - ${v?.note} - ${v.code}`,
                            value: v?._id?.toString(),
                        }))}
                    name="selectedShop"
                    value={selectedShop || ""}
                    onChange={(data) => {
                        setSelectedShop(data.value || "");
                    }}
                    placeholder={t("Select shop")}
                />
            </div>
            {/* Tabs Bootstrap để đổi widget */}
            {configData && tokenData && (
                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${currentWidget === "sv_overview" ? "active" : ""}`}
                            onClick={() => setCurrentWidget("sv_overview")}
                        >
                            {t("Overview")}
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${currentWidget === "sv_get_inspired" ? "active" : ""}`}
                            onClick={() => setCurrentWidget("sv_get_inspired")}
                        >
                            {t("Inspiration")}
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${currentWidget === "sv_create_content" ? "active" : ""}`}
                            onClick={() => setCurrentWidget("sv_create_content")}
                        >
                            {t("Create Content")}
                        </button>
                    </li>
                </ul>
            )}

            <div className="d-flex justify-content-center">
                {/* Render widget khi có config + token */}
                {configData && tokenData ? (
                    <WidgetConfigProvider
                        key={selectedShop}
                        config={configData}
                        getToken={getToken}
                        remotes={[
                            { name: "@tiktokshop-widget/merchant:OverviewWidget" },
                            { name: "@tiktokshop-widget/merchant:InspirationWidget" },
                            { name: "@tiktokshop-widget/merchant:MakeContentWidget" },
                            // { name: "@tiktokshop-widget/reverse:ManageReturns" },
                            // { name: "@tiktokshop-widget/logistics:warehouse" },
                            // { name: "@tiktokshop-widget/logistics:shippingTemplate" },
                            // { name: "@tiktokshop-widget/product:optimizer" },
                        ]}
                        preloadWidgetNames={[
                            "@tiktokshop-widget/merchant:OverviewWidget",
                            "@tiktokshop-widget/merchant:InspirationWidget",
                            "@tiktokshop-widget/merchant:MakeContentWidget",
                            // "@tiktokshop-widget/reverse:ManageReturns",
                            // "@tiktokshop-widget/logistics:warehouse",
                            // "@tiktokshop-widget/logistics:shippingTemplate",
                            // "@tiktokshop-widget/product:optimizer",
                        ]}
                    >
                        {currentWidget === "sv_overview" && (
                            <RemoteWidget
                                name="@tiktokshop-widget/merchant:OverviewWidget"
                                options={widgetParams}
                            />
                        )}
                        {currentWidget === "sv_get_inspired" && (
                            <RemoteWidget
                                name="@tiktokshop-widget/merchant:InspirationWidget"
                                options={widgetParams}
                            />
                        )}
                        {currentWidget === "sv_create_content" && (
                            <RemoteWidget
                                name="@tiktokshop-widget/merchant:MakeContentWidget"
                                options={widgetParams}
                            />
                        )}
                    </WidgetConfigProvider>
                ) : (
                    selectedShop && (
                        <div className="d-flex justify-content-center align-items-center">
                            Loading widget...
                        </div>
                    )
                )}
            </div>

        </div>
    );
};

const mapStateToProps = ({ shop }) => ({ shop });
const mapDispatchToProps = { getShops, getWidgetToken };

export default connect(mapStateToProps, mapDispatchToProps)(ProductMerchantWidgets);
